import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BannerFichas from '../../components/BannerFichas/BannerFichas';
import Navbar from '../../components/Navbar/Navbar';
import SessionBar from '../../components/SessionBar/SessionBar';
import Footer from '../../components/Footer/Footer';
import BotonChico03 from '../../components/Botones/BotonChico03/BotonChico03';
import { fetchUserData } from '../../utils/api';
import axiosInstance from '../../services/AxiosInstance';
import { ROUTES } from '../../const/routes';
import { useUser } from '../../context/UserContext';
import { getAvatarSrc, handleAvatarError } from '../../utils/avatar';
import styles from './Ranking.module.css';
import { useTranslation } from 'react-i18next';

const JUGADORES_POR_PAGINA = 10;

const formatCurrency = (value) =>
  value.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getPodiumClasses = (globalPosition) => {
  switch (globalPosition) {
    case 1:
      return 'bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 text-slate-900';
    case 2:
      return 'bg-gradient-to-r from-slate-400 via-slate-300 to-slate-400 text-slate-900';
    case 3:
      return 'bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 text-slate-900';
    default:
      return '';
  }
};

const Ranking = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData: currentUser } = useUser();
  const [userData, setUserData] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaActiva, setBusquedaActiva] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const handleBusquedaChange = (e) => {
    const valor = e.target.value;
    setBusqueda(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setBusquedaActiva(valor.trim());
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchRanking = useCallback(async () => {
    setCargando(true);
    try {
      const params = { page: currentPage, limit: JUGADORES_POR_PAGINA };
      if (busquedaActiva) params.search = busquedaActiva;
      const response = await axiosInstance.get('/api/usuario/ranking', {
        params,
      });
      const { data, totalPages } = response.data;
      setRanking(data);
      setTotalPaginas(totalPages || 1);
    } catch (error) {
      console.error('Error al obtener el ranking:', error);
      setRanking([]);
      setTotalPaginas(1);
    } finally {
      setCargando(false);
    }
  }, [currentPage, busquedaActiva]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  return (
    <div className="flex flex-col min-h-screen">
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 flex-grow">
        <Navbar />
        {userData && <SessionBar />}
        <div className="flex flex-col items-center py-6 md:py-10 px-2 lg:px-10">
          <h2 className="text-lg md:text-xl lg:text-2xl text-yellow-500 font-bold mb-3 md:mb-4">
            {t('ranking.title')}
          </h2>
          <p className="text-[10px] md:text-xs lg:text-sm text-gray-300 mb-4 md:mb-8 text-center">
            {t('ranking.subtitle')}
          </p>

          {/* Buscador de usuario */}
          <div className="w-full max-w-3xl mb-4">
            <div className="relative">
              <input
                type="text"
                value={busqueda}
                onChange={handleBusquedaChange}
                placeholder={t('ranking.searchPlaceholder')}
                maxLength={30}
                className="w-full bg-slate-800/80 border-2 border-slate-600 rounded-lg px-4 py-2 pl-10
                  text-sm text-gray-200 placeholder-gray-500
                  focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50
                  transition-all duration-200"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {busqueda && (
                <button
                  onClick={() => {
                    setBusqueda('');
                    setCurrentPage(1);
                    setBusquedaActiva('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="w-full max-w-3xl">
            <div
              className={`bg-bgContenedorOscuro rounded-lg px-2 md:px-5
                border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)]`}
            >
              {/* Encabezado - Desktop */}
              <div className="hidden md:grid grid-cols-[50px_56px_1fr_200px_80px] gap-4 text-yellow-500 font-bold text-sm py-4 px-4 border-b-2 border-slate-700">
                <div className="text-center">{t('ranking.columnPosition')}</div>
                <div className="text-center"></div>
                <div className="text-left">{t('ranking.columnPlayer')}</div>
                <div className="text-center">{t('ranking.columnBalance')}</div>
                <div className="text-center">{t('ranking.columnResets')}</div>
              </div>

              {/* Contenido */}
              {cargando ? (
                <div className="text-center text-gray-300 py-10 animate-pulse">
                  {t('ranking.loading')}
                </div>
              ) : ranking.length === 0 ? (
                <div className="text-center text-gray-300 py-10">
                  {busquedaActiva
                    ? t('ranking.noResults', { search: busquedaActiva })
                    : t('ranking.noPlayers')}
                </div>
              ) : (
                <>
                  {/* Vista Desktop */}
                  <div className="hidden md:block">
                    {ranking.map((jugador, index) => {
                      const globalPosition =
                        jugador.posicion ??
                        (currentPage - 1) * JUGADORES_POR_PAGINA + index + 1;
                      const isPodium = globalPosition <= 3;
                      const isCurrentUser = currentUser?.id === jugador.id;
                      const avatarSrc = getAvatarSrc(jugador.avatar);

                      return (
                        <div
                          key={jugador.id}
                          className={`
                            grid grid-cols-[50px_56px_1fr_200px_80px] gap-4 items-center py-3 px-4
                            ${isPodium ? getPodiumClasses(globalPosition) : index % 2 === 0 ? 'bg-slate-800/60' : 'bg-slate-800/30'}
                            ${isCurrentUser ? 'ring-2 ring-yellow-400 ring-inset' : ''}
                            ${styles.rankingRow}
                            text-white border-b border-slate-700/40 transition-all duration-200 hover:brightness-110
                          `}
                        >
                          <div
                            className={`text-center font-bold text-base ${isPodium ? '' : 'text-gray-400'}`}
                          >
                            {globalPosition}
                          </div>
                          <div className="flex justify-center">
                            <img
                              src={avatarSrc}
                              alt={`Avatar de ${jugador.nick_usuario}`}
                              onError={handleAvatarError}
                              className={`w-10 h-10 rounded-full border-2 bg-gray-600 object-cover ${
                                isPodium
                                  ? 'border-slate-900/50'
                                  : 'border-slate-500'
                              }`}
                            />
                          </div>
                          <div
                            className={`font-semibold flex items-center gap-2 min-w-0 text-left ${isPodium ? 'text-base' : 'text-sm text-gray-200'}`}
                          >
                            <button
                              onClick={() =>
                                navigate(
                                  `${ROUTES.historialJugador}/${jugador.id}`
                                )
                              }
                              className="underline hover:text-yellow-400 transition-colors cursor-pointer text-left truncate min-w-0"
                            >
                              {jugador.nick_usuario}
                            </button>
                            {isCurrentUser && (
                              <span className="flex-shrink-0 text-xs bg-white/90 text-slate-900 px-2 py-0.5 rounded-full border border-white/60 font-bold">
                                {t('ranking.youBadge')}
                              </span>
                            )}
                          </div>
                          <div
                            className={`text-center font-bold tabular-nums whitespace-nowrap ${isPodium ? 'text-base' : 'text-sm text-emerald-400'}`}
                          >
                            $ {formatCurrency(jugador.saldo)}
                          </div>
                          <div
                            className={`text-center tabular-nums ${isPodium ? 'text-sm font-semibold' : 'text-sm text-orange-400'}`}
                          >
                            {jugador.reset ?? 0}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Vista Movil - Tarjetas */}
                  <div className="md:hidden py-4">
                    {ranking.map((jugador, index) => {
                      const globalPosition =
                        jugador.posicion ??
                        (currentPage - 1) * JUGADORES_POR_PAGINA + index + 1;
                      const isPodium = globalPosition <= 3;
                      const isCurrentUser = currentUser?.id === jugador.id;
                      const avatarSrc = getAvatarSrc(jugador.avatar);

                      return (
                        <div
                          key={jugador.id}
                          className={`
                            ${isPodium ? getPodiumClasses(globalPosition) : index % 2 === 0 ? 'bg-slate-800/60' : 'bg-slate-800/30'}
                            ${isCurrentUser ? 'ring-2 ring-yellow-400 ring-inset' : ''}
                            rounded-lg p-2 sm:p-3 mb-3 text-white
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 text-center font-bold text-sm flex-shrink-0 ${isPodium ? '' : 'text-gray-400'}`}
                            >
                              {globalPosition}
                            </div>
                            <img
                              src={avatarSrc}
                              alt={`Avatar de ${jugador.nick_usuario}`}
                              onError={handleAvatarError}
                              className={`w-9 h-9 rounded-full border-2 bg-gray-600 flex-shrink-0 object-cover ${
                                isPodium
                                  ? 'border-slate-900/50'
                                  : 'border-slate-500'
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-semibold flex items-center gap-1 min-w-0 ${isPodium ? 'text-xs' : 'text-xs text-gray-200'}`}
                              >
                                <button
                                  onClick={() =>
                                    navigate(
                                      `${ROUTES.historialJugador}/${jugador.id}`
                                    )
                                  }
                                  className="underline hover:text-yellow-400 transition-colors cursor-pointer text-left truncate min-w-0"
                                >
                                  {jugador.nick_usuario}
                                </button>
                                {isCurrentUser && (
                                  <span className="flex-shrink-0 text-[9px] bg-white/90 text-slate-900 px-1.5 py-0.5 rounded-full border border-white/60 font-bold">
                                    {t('ranking.youBadge')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className={`text-right font-bold text-xs tabular-nums flex-shrink-0 whitespace-nowrap ${isPodium ? '' : 'text-emerald-400'}`}
                            >
                              $ {formatCurrency(jugador.saldo)}
                            </div>
                            <div
                              className={`text-right text-[10px] tabular-nums flex-shrink-0 ${isPodium ? '' : 'text-orange-400'}`}
                            >
                              R: {jugador.reset ?? 0}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Controles de paginacion */}
              <div className="flex flex-wrap justify-center items-center my-6 md:my-10 gap-2 md:gap-4">
                <BotonChico03
                  texto={t('ranking.previousButton')}
                  bgColor={currentPage === 1 ? 'gray' : 'pink'}
                  funcion={() => {
                    if (currentPage > 1) {
                      setCurrentPage((prev) => prev - 1);
                    }
                  }}
                />
                <span className="text-gray-300 py-1 px-3 whitespace-nowrap text-xs md:text-base">
                  {currentPage} / {totalPaginas}
                </span>
                <BotonChico03
                  texto={t('ranking.nextButton')}
                  bgColor={currentPage === totalPaginas ? 'gray' : 'blue'}
                  funcion={() => {
                    if (currentPage < totalPaginas) {
                      setCurrentPage((prev) => prev + 1);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Ranking;
